import axios, { AxiosInstance } from 'axios'
import { getCsrfToken } from './utils'

// Determine the correct backend URL based on environment
function getBackendURL(): string {
  // If VITE_API_URL is explicitly set, use it (remove trailing /api)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/api$/, '')
  }
  
  // Auto-detect based on current hostname
  const hostname = window.location.hostname
  
  // Development environment (localhost with Vite dev server)
  if (hostname === 'localhost' || hostname.includes('.localhost')) {
    // Use empty string to leverage Vite's proxy configuration
    return ''
  }
  
  // Production environment (devfctrz.com)
  if (hostname === 'app.devfctrz.com' || hostname.endsWith('.devfctrz.com')) {
    // Point to API subdomain
    return 'https://api.devfctrz.com'
  }
  
  // Fallback: use same origin (shouldn't happen in normal cases)
  console.warn('[Knowledge API] Using same-origin fallback for API calls')
  return ''
}

const BACKEND_URL = getBackendURL()

// Debug: Log the knowledge API URL being used
console.log('[Knowledge API Config] VITE_API_URL:', import.meta.env.VITE_API_URL)
console.log('[Knowledge API Config] Detected hostname:', window.location.hostname)
console.log('[Knowledge API Config] BACKEND_URL:', BACKEND_URL)

// Create axios instance for knowledge base endpoints (which are at /knowledge/ not /api/)
const knowledgeApi: AxiosInstance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

// Extract tenant subdomain from stored auth data (localStorage)
// Fallback to URL subdomain for backward compatibility
function getTenantSubdomain(): string | null {
  // Priority 1: Get from localStorage (set during login)
  try {
    const stored = localStorage.getItem('tenant')
    if (stored) {
      const tenant = JSON.parse(stored)
      if (tenant.subdomain) {
        return tenant.subdomain
      }
    }
  } catch (e) {
    console.error('[Knowledge API] Error reading tenant from localStorage:', e)
  }
  
  // Priority 2: Fallback to URL subdomain (backward compatibility)
  const hostname = window.location.hostname
  
  // Skip if we're on the static app domain
  if (hostname === 'app.devfctrz.com' || hostname === 'app.localhost') {
    return null
  }
  
  // Extract subdomain from hostname
  if (hostname.includes('.devfctrz.com')) {
    const parts = hostname.split('.devfctrz.com')[0]
    return parts.includes('.') ? null : parts
  }
  if (hostname.includes('.localhost')) {
    const parts = hostname.split('.localhost')[0]
    return parts.includes('.') ? null : parts
  }
  
  return null
}

// Add CSRF token and tenant subdomain to all requests
knowledgeApi.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken
  }
  
  // Add tenant subdomain header for cross-domain API calls
  const tenantSubdomain = getTenantSubdomain()
  if (tenantSubdomain) {
    config.headers['X-Tenant-Subdomain'] = tenantSubdomain
  }
  
  // For FormData uploads, remove Content-Type so axios can set multipart/form-data with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  
  return config
})

// Response interceptor for error handling
knowledgeApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default knowledgeApi
